import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import AuthShell from '../../components/marketing/AuthShell';
import { useLogin, useLoginWithPhone, useVerifyPhoneOtp } from '../../api/hooks';
import { useLandSession } from '../../hooks';
import { plainError } from '../../lib/format';
import { clearError } from '../../state/authSlice';

const linkStyle = { background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--brand)', fontWeight: 700, cursor: 'pointer' };

const keyStyle = {
  border: 'none', background: '#eef1f4', color: '#3c4552', borderRadius: 6, height: 26, minWidth: 28, padding: '0 7px',
  font: 'inherit', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', lineHeight: 1,
};

/**
 * An input with its own editing keys inside it, on the right: ⌫ takes off the
 * last character, ✕ clears the field, and a password can be shown while it is
 * typed. For a phone keypad or a pasted number, where a backspace key is not
 * always to hand. Focus goes back to the field after each.
 */
function EditableInput({ value, onChange, reveal = false, disabled = false, type, ...rest }) {
  const [shown, setShown] = useState(false);
  const ref = useRef(null);
  const set = (next) => {
    onChange(next);
    ref.current?.focus();
  };
  const has = Boolean(value) && !disabled;
  const keys = (has ? 2 : 0) + (reveal ? 1 : 0);
  return (
    <div style={{ position: 'relative' }}>
      <input
        {...rest}
        ref={ref}
        type={reveal && shown ? 'text' : type}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{ paddingRight: keys ? 12 + keys * 34 : undefined }}
      />
      {keys > 0 && (
        <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 4 }}>
          {has && (
            <>
              <button type="button" style={keyStyle} aria-label="Delete the last character" title="Delete one" onClick={() => set(value.slice(0, -1))}>⌫</button>
              <button type="button" style={keyStyle} aria-label="Clear" title="Clear" onClick={() => set('')}>✕</button>
            </>
          )}
          {reveal && (
            <button type="button" style={keyStyle} aria-label={shown ? 'Hide the password' : 'Show the password'} onClick={() => { setShown((v) => !v); ref.current?.focus(); }}>
              {shown ? 'Hide' : 'Show'}
            </button>
          )}
        </span>
      )}
    </div>
  );
}

/**
 * Sign in. The Google Workspace and SSO buttons are the design's and stay
 * inert, as the static page shipped them.
 *
 * Forgot-password is live: it carries whatever address has been typed over to
 * that screen, so somebody who mistyped their password does not retype their
 * email as well.
 */

const FACTS = [
  ['Your morning read is ready.', 'Since your last visit, 3 holdings changed alpha percentile and 2 client portfolios crossed a drift threshold. The book verdict is waiting.'],
  ['Markets moved. Your verdicts moved with them.', 'Every portfolio carries its own health score, attribution and risk in rupees. Log in to see what changed — and what to do about it.'],
  ['One number changed that matters.', 'A fund held by 34 of your clients slipped below the 40th alpha percentile for the second straight quarter. The switch-review is drafted.'],
];

/* The panel's sample morning read — the dashboard's own alert kinds. */
const DIGEST = [
  ['neg', 'Detractor across the book', 'HDFC Mid-Cap Opp. · 34 portfolios'],
  ['warn', 'Allocation drift', '6 clients off their policy target'],
  ['pos', 'Goal ahead of schedule', 'Iyer · child’s education, 112% funded'],
];

export default function Login() {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  /* Why they are here, when they did not come here on purpose: a session that
     expired mid-task leaves its reason on the store. Shown once, then cleared,
     so it does not haunt the next visit to this page. */
  const expiredReason = useSelector((s) => s.auth.error);
  useEffect(() => () => { dispatch(clearError()); }, [dispatch]);
  const [fact] = useState(() => FACTS[Math.floor(Math.random() * FACTS.length)]);
  const [form, setForm] = useState({ email: '', password: '' });
  const [failure, setFailure] = useState(null);
  const [login, { loading }] = useLogin();
  const land = useLandSession();
  /* BoloDhan's own way in. The phone number, then the code it was sent; the
     session id the first call mints must go back with the code. */
  const [mode, setMode] = useState('phone');
  const [otp, setOtp] = useState({ phone: '', code: '', sessionId: null, sent: false, sentTo: null, message: null });
  /* The code box is for the number the code went to: changing the number hides
     it (a new code is needed), and typing that same number back brings it back. */
  const awaiting = otp.sent && otp.phone.trim() === otp.sentTo;
  const [loginWithPhone, { loading: sending }] = useLoginWithPhone();
  const [verifyPhoneOtp, { loading: verifying }] = useVerifyPhoneOtp();

  const sendOtp = async (e) => {
    e?.preventDefault?.();
    setFailure(null);
    const phoneNumber = otp.phone.trim();
    if (!phoneNumber) return;
    try {
      const { data } = await loginWithPhone({ variables: { input: { phoneNumber } } });
      const res = data?.loginWithPhone;
      if (!res?.success || !res.sessionId) throw new Error(res?.message || 'The code could not be sent');
      setOtp({ ...otp, code: '', sessionId: res.sessionId, sent: true, sentTo: phoneNumber, message: res.message || 'OTP sent to your phone' });
    } catch (err) {
      setFailure(plainError(err, 'The code could not be sent'));
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setFailure(null);
    try {
      const { data } = await verifyPhoneOtp({
        variables: { input: { phoneNumber: otp.phone.trim(), otp: otp.code.trim(), sessionId: otp.sessionId, deviceType: 'web' } },
      });
      const auth = data?.verifyPhoneLoginOtp;
      if (!auth?.success || !auth.authToken) throw new Error(auth?.message || 'The code was not accepted');
      land(auth, null);
    } catch (err) {
      setFailure(plainError(err, 'The code was not accepted'));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setFailure(null);
    try {
      const { data } = await login({ variables: { email: form.email.trim(), password: form.password } });
      const auth = data?.loginUser;
      if (!auth?.success || !auth.authToken) throw new Error(auth?.message || 'Sign in failed');
      land(auth, null);
    } catch (err) {
      setFailure(plainError(err, 'Sign in failed'));
    }
  };

  /* Older marketing CTAs arrive as /login?mode=signup; signup is its own page. */
  if (params.get('mode') === 'signup') return <Navigate to="/signup" replace />;

  return (
    <AuthShell
      aside={(
        <>
          <div className="lx-auth-say">
            <h2>{fact[0]}</h2>
            <p className="read">{fact[1]}</p>
            <div className="lx-digest" aria-hidden="true">
              <div className="lx-digest-h"><span><i />Morning read</span><em>today</em></div>
              {DIGEST.map(([kind, title, body], i) => (
                <div className={`lx-digest-row k-${kind}`} key={title} style={{ '--d': `${0.55 + i * 0.16}s` }}>
                  <i />
                  <div><b>{title}</b><span>{body}</span></div>
                </div>
              ))}
              <div className="lx-digest-f">2 fixes drafted for the Sharma family →</div>
            </div>
            <ul className="lx-auth-ticks">
              <li><b>Verdict-first</b> — every login opens on what needs you today</li>
              <li><b>As of any date</b> — see a ranking as it stood on any past day</li>
              <li><b>A copilot on every screen</b> — ask about what you are looking at</li>
            </ul>
          </div>
          <span style={{ fontSize: 12, color: '#8b90a8' }}>© 2026 Arthaprajna Private Limited</span>
        </>
      )}
    >
      <h1>Welcome back</h1>
      {expiredReason && !failure && <div className="auth-msg bad">{expiredReason}</div>}
      <div className="sub">Log in to your workspace. Your role decides your landing page — MFDs open the book verdict, PMs open their fund, CIOs open the firm.</div>
      <p style={{ fontSize: 12.5, color: 'var(--ink3)', lineHeight: 1.55, marginBottom: 14 }}>
        Any BoloDhan account signs in here — with the phone number and OTP the app uses, or an email and
        password. A client lands on their own portfolios, goals and trading accounts; a firm's advisor on their desk.
      </p>
      {/* The marketing shell does not carry the app's `.tabs` styles, so the
          switch is styled here: two segments, the chosen one filled. */}
      <div role="tablist" aria-label="How to sign in" style={{ display: 'flex', gap: 4, background: '#eef1f4', padding: 4, borderRadius: 10, marginBottom: 16, width: 'fit-content' }}>
        {[['phone', 'Phone & OTP'], ['email', 'Email & password']].map(([id, label]) => {
          const on = mode === id;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => { setMode(id); setFailure(null); }}
              style={{
                padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 13, fontWeight: 600,
                background: on ? '#fff' : 'transparent', color: on ? '#0f1729' : '#5b6675', boxShadow: on ? '0 1px 3px rgba(15,23,41,.12)' : 'none',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {mode === 'phone' && (
        <form onSubmit={awaiting ? verifyOtp : sendOtp}>
          <div className="field">
            <label>Mobile number</label>
            <EditableInput
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              placeholder="+91 98765 43210"
              required
              value={otp.phone}
              onChange={(v) => setOtp({ ...otp, phone: v })}
            />
          </div>
          {awaiting && (
            <div className="field">
              <label>One-time code</label>
              <EditableInput
                type="text"
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="6-digit code"
                required
                autoFocus
                value={otp.code}
                onChange={(v) => setOtp({ ...otp, code: v.replace(/\D/g, '').slice(0, 8) })}
              />
            </div>
          )}
          {awaiting && otp.message && <div className="auth-msg" style={{ marginTop: 10 }}>{otp.message}</div>}
          <button className="btn pri lg" style={{ width: '100%', marginTop: 22 }} type="submit" disabled={sending || verifying}>
            {sending || verifying ? 'Please wait…' : awaiting ? 'Verify & log in →' : 'Send OTP →'}
          </button>
          {awaiting && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 13 }}>
              <button type="button" style={linkStyle} onClick={() => setOtp({ phone: otp.phone, code: '', sessionId: null, sent: false, sentTo: null, message: null })}>Change number</button>
              <button type="button" style={linkStyle} onClick={sendOtp} disabled={sending}>Resend code</button>
            </div>
          )}
          {failure && <div className="auth-msg bad">{failure}</div>}
        </form>
      )}

      {mode === 'email' && (
      <form onSubmit={submit}>
        <div className="field">
          <label>Email</label>
          <EditableInput type="email" autoComplete="email" placeholder="you@example.com" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
        </div>
        <div className="field">
          <label>Password</label>
          <EditableInput reveal type="password" autoComplete="current-password" placeholder="••••••••" required value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14, fontSize: 13.5 }}>
          <Link
            to={form.email.trim() ? `/forgot-password?email=${encodeURIComponent(form.email.trim())}` : '/forgot-password'}
            style={{ color: 'var(--brand)', fontWeight: 700 }}
          >
            Forgot password?
          </Link>
        </div>
        <button className="btn pri lg" style={{ width: '100%', marginTop: 22 }} type="submit" disabled={loading}>
          {loading ? 'Please wait…' : 'Log in →'}
        </button>
        {failure && <div className="auth-msg bad">{failure}</div>}
      </form>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0', color: 'var(--ink3)', fontSize: 12 }}>
        <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />or<span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
      </div>
      <button className="btn" style={{ width: '100%' }} type="button">Continue with Google Workspace</button>
      <button className="btn" style={{ width: '100%', marginTop: 10 }} type="button">SSO (Organization members)</button>
      <div className="auth-alt">New to KeepInvesting? <Link to="/signup">Start your free trial</Link></div>
    </AuthShell>
  );
}
